import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { fmtMoney, fmtDateTime, PAYMENT_METHODS } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { Modal, Field, inputCls, selectCls, btnPrimary, btnSecondary } from "../components/Modal";
import { toast } from "sonner";
import { Plus, RotateCcw } from "lucide-react";

export default function Returns() {
  const { user, hasPerm } = useAuth();
  const cur = user?.currency || "FCFA";
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("sale");
  const [sources, setSources] = useState([]);
  const [sourceId, setSourceId] = useState("");
  const [source, setSource] = useState(null);
  const [products, setProducts] = useState({});
  const [lines, setLines] = useState([]);
  const [reason, setReason] = useState("");
  const [refundMethod, setRefundMethod] = useState("");
  const [restock, setRestock] = useState(true);
  const [busy, setBusy] = useState(false);
  const [prevReturns, setPrevReturns] = useState({});

  const load = () => api.get("/returns", { params: { limit: 100 } }).then((r) => setRows(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  // Chargement des documents d'origine selon le type
  useEffect(() => {
    if (!open) return;
    if (kind === "sale") {
      api.get("/sales", { params: { limit: 100 } })
        .then((r) => setSources(r.data.items.filter((s) => s.status === "validated")))
        .catch(() => {});
    } else {
      api.get("/purchases")
        .then((r) => setSources(r.data.filter((p) => p.status !== "cancelled")))
        .catch(() => {});
    }
  }, [kind, open]);

  const openReturn = async (presetKind, presetId) => {
    setKind(presetKind || "sale");
    setReason(""); setRefundMethod(""); setRestock(true);
    setLines([]); setSource(null); setSourceId(presetId || "");
    setOpen(true);
    if (presetId) pickSource(presetId, presetKind || "sale");
  };

  useEffect(() => {
    const sale = params.get("sale");
    const purchase = params.get("purchase");
    if (sale) openReturn("sale", sale);
    else if (purchase) openReturn("purchase", purchase);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const pickSource = async (id, k = kind) => {
    setSourceId(id);
    if (!id) { setSource(null); setLines([]); return; }
    try {
      const { data } = await api.get(k === "sale" ? `/sales/${id}` : `/purchases/${id}`);
      setSource(data);
      const ret = await api.get("/returns", { params: { source_id: id, limit: 100 } });
      const already = {};
      ret.data.items.forEach((r) => r.items.forEach((ri) => { already[ri.product_id] = (already[ri.product_id] || 0) + ri.qty_base; }));
      setPrevReturns(already);
      const prods = {};
      await Promise.all([...new Set(data.items.map((i) => i.product_id))].map(async (pid) => {
        try { prods[pid] = (await api.get(`/products/${pid}`)).data; }
        catch (e) { console.warn(`Produit ${pid} non chargé pour le retour`, e); }
      }));
      setProducts(prods);
      setLines(data.items.filter((l, i, arr) => arr.findIndex((x) => x.product_id === l.product_id) === i)
        .map((l) => ({ product_id: l.product_id, name: l.name, qty: 0, unit: l.unit || "",
          sold_base: k === "purchase" ? (l.received_qty || 0) * (l.factor || 1) : l.qty_base,
          sold_label: `${l.qty} ${l.unit || ""}` })));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const factorOf = (l) => {
    const p = products[l.product_id];
    if (!p || !l.unit || l.unit === p.unit) return 1;
    const u = (p.units || []).find((x) => x.unit === l.unit);
    return u ? Number(u.factor) || 1 : 1;
  };
  const remaining = (l) => l.sold_base - (prevReturns[l.product_id] || 0);
  const refundTotal = lines.reduce((s, l) => {
    if (!source) return s;
    const src = source.items.find((x) => x.product_id === l.product_id);
    if (!src || !Number(l.qty)) return s;
    const lineFactor = src.factor || (src.qty_base / src.qty) || 1;
    return s + (src.unit_price / lineFactor) * factorOf(l) * Number(l.qty);
  }, 0);

  const submit = async () => {
    const items = lines.filter((l) => Number(l.qty) > 0).map((l) => ({ product_id: l.product_id, qty: Number(l.qty), unit: l.unit }));
    if (!items.length) { toast.error("Saisissez au moins une quantité à retourner"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/returns", { kind, source_id: sourceId, items, reason, refund_method: refundMethod, restock });
      toast.success(`Retour ${data.number} enregistré — ${fmtMoney(data.refund_amount, cur)}`);
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const columns = [
    { key: "number", label: "N°", render: (r) => <span className="font-money font-bold">{r.number}</span> },
    { key: "kind", label: "Type", render: (r) => (
      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${r.kind === "sale" ? "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300" : "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300"}`}>
        {r.kind === "sale" ? "Retour client" : "Retour fournisseur"}
      </span>) },
    { key: "source_number", label: "Document", render: (r) => <span className="font-money">{r.source_number}</span> },
    { key: "who", label: "Client / Fournisseur", render: (r) => r.customer_name || r.supplier_name || "—" },
    { key: "items", label: "Articles", render: (r) => (
      <div className="text-xs">{r.items.map((i, k) => <div key={`${i.product_id}-${i.unit || k}`}>{i.qty} {i.unit} · {i.name}</div>)}</div>) },
    { key: "refund_amount", label: "Montant", className: "text-right", render: (r) => <span className="font-money font-bold">{fmtMoney(r.refund_amount, cur)}</span> },
    { key: "restock", label: "Stock", render: (r) => r.restock
      ? <span className="text-xs font-bold text-emerald-600">Remis en stock</span>
      : <span className="text-xs font-bold text-rose-600">Non remis</span> },
    { key: "reason", label: "Motif", render: (r) => <span className="text-xs text-muted-foreground">{r.reason || "—"}</span> },
    { key: "created_at", label: "Date", render: (r) => <span className="text-xs">{fmtDateTime(r.created_at)}</span> },
    { key: "created_by_name", label: "Par" },
  ];

  return (
    <div className="space-y-4" data-testid="returns-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Retours</h1>
        {hasPerm("returns.manage") && (
          <button data-testid="return-create" onClick={() => openReturn("sale")} className={btnPrimary}><Plus className="h-4 w-4" /> Nouveau retour</button>)}
      </div>
      <DataTable testid="returns-table" columns={columns} rows={rows} searchKeys={["number", "source_number", "customer_name", "supplier_name", "reason"]} pageSize={15}
        empty="Aucun retour enregistré" />

      <Modal open={open} onClose={() => setOpen(false)} title="Nouveau retour" wide>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type de retour">
              <select data-testid="return-kind" value={kind} onChange={(e) => { setKind(e.target.value); setSourceId(""); setSource(null); setLines([]); }} className={selectCls}>
                <option value="sale">Retour client (vente)</option>
                <option value="purchase">Retour fournisseur (achat)</option>
              </select>
            </Field>
            <Field label={kind === "sale" ? "Vente / Facture d'origine" : "Achat d'origine"}>
              <select data-testid="return-source" value={sourceId} onChange={(e) => pickSource(e.target.value)} className={selectCls}>
                <option value="">— Sélectionner —</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.number} · {kind === "sale" ? (s.customer_name || "Comptant") : (s.supplier_name || "")} · {fmtMoney(s.total, cur)}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {source && (
            <div className="space-y-2" data-testid="return-lines">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Articles à retourner</div>
              {lines.map((l, i) => {
                const p = products[l.product_id];
                const rem = remaining(l);
                return (
                  <div key={l.product_id} className="border rounded-lg p-2 flex items-center gap-2 flex-wrap text-sm" data-testid={`return-line-${i}`}>
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-semibold">{l.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {kind === "sale" ? "Vendu" : "Reçu"} : {l.sold_label} · Retournable max : {rem}
                      </div>
                    </div>
                    <input data-testid={`return-qty-${i}`} type="number" min="0" step="any" value={l.qty}
                      onChange={(e) => setLine(i, "qty", e.target.value)} className={`${inputCls} w-20`} placeholder="Qté" />
                    {(p?.units || []).length > 0 && (
                      <select data-testid={`return-unit-${i}`} value={l.unit} onChange={(e) => setLine(i, "unit", e.target.value)} className={`${selectCls} w-32`}>
                        <option value={p.unit}>{p.unit}</option>
                        {p.units.map((u) => <option key={u.unit} value={u.unit}>{u.unit}</option>)}
                      </select>
                    )}
                  </div>
                );
              })}
              <div className="text-right font-money font-bold" data-testid="return-total">Remboursement : {fmtMoney(refundTotal, cur)}</div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Mode de remboursement">
              <select data-testid="return-refund-method" value={refundMethod} onChange={(e) => setRefundMethod(e.target.value)} className={selectCls}>
                <option value="">Avoir (déduit de la dette)</option>
                {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Motif">
              <input data-testid="return-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} placeholder="Produit défectueux, erreur…" />
            </Field>
          </div>
          <label className="inline-flex items-center gap-2 text-sm font-semibold">
            <input data-testid="return-restock" type="checkbox" checked={restock} onChange={(e) => setRestock(e.target.checked)} />
            Remettre les articles en stock
          </label>
          <div className="flex gap-2">
            <button data-testid="return-cancel" onClick={() => setOpen(false)} className={`${btnSecondary} flex-1`}>Annuler</button>
            <button data-testid="return-submit" onClick={submit} disabled={busy || !source} className={`${btnPrimary} flex-1`}>
              <RotateCcw className="h-4 w-4" /> {busy ? "Enregistrement..." : "Valider le retour"}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
